<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from './AnalyticsPanel.vue';
import MetricValue from './MetricValue.vue';
import ConversationLink from './ConversationLink.vue';
import { useAnalyticsFormat } from './useAnalyticsFormat';

const props = defineProps({
  errors: { type: Object, default: null }, // { turn, retry, dead }
  items: { type: Array, default: null }, // [{ conversation_id, at, kind, detail, agent_slug }]
  agentNames: { type: Object, default: () => ({}) },
  timezone: { type: String, default: undefined },
  runtimeReason: { type: String, default: '' },
});

const RECENT_LIMIT = 5;

const { t } = useI18n();
const { number, dateTime } = useAnalyticsFormat();

const kinds = computed(() => [
  {
    key: 'turn',
    label: t('ANALYTICS_REPORTS.ERRORS.TURN'),
    hint: t('ANALYTICS_REPORTS.ERRORS.TURN_HINT'),
    item: t('ANALYTICS_REPORTS.ERRORS.ITEM_TURN'),
  },
  {
    key: 'retry',
    label: t('ANALYTICS_REPORTS.ERRORS.RETRY'),
    hint: t('ANALYTICS_REPORTS.ERRORS.RETRY_HINT'),
    item: t('ANALYTICS_REPORTS.ERRORS.ITEM_RETRY'),
  },
  {
    key: 'dead',
    label: t('ANALYTICS_REPORTS.ERRORS.DEAD'),
    hint: t('ANALYTICS_REPORTS.ERRORS.DEAD_HINT'),
    item: t('ANALYTICS_REPORTS.ERRORS.ITEM_DEAD'),
  },
]);

const recent = computed(() => (props.items || []).slice(0, RECENT_LIMIT));

// Tipo que a tela não conhece aparece com o nome cru, em vez de sumir da lista.
const kindLabel = kind =>
  kinds.value.find(item => item.key === kind)?.item || kind;
</script>

<template>
  <AnalyticsPanel
    :title="t('ANALYTICS_REPORTS.ERRORS.TITLE')"
    :description="t('ANALYTICS_REPORTS.ERRORS.DESCRIPTION')"
  >
    <dl class="grid gap-3 sm:grid-cols-3">
      <div v-for="kind in kinds" :key="kind.key" class="min-w-0">
        <dt class="text-xs text-n-slate-11">{{ kind.label }}</dt>
        <dd>
          <MetricValue
            :value="errors?.[kind.key] ?? null"
            :format="number"
            :reason="runtimeReason"
            class="text-xl font-medium tabular-nums text-n-slate-12"
          />
          <p class="mb-0 text-xs text-n-slate-11">{{ kind.hint }}</p>
        </dd>
      </div>
    </dl>

    <div v-if="recent.length" class="flex flex-col gap-2 min-w-0">
      <h3 class="text-xs font-medium text-n-slate-11">
        {{ t('ANALYTICS_REPORTS.ERRORS.RECENT') }}
      </h3>
      <ul class="flex flex-col list-none divide-y divide-n-weak">
        <li
          v-for="item in recent"
          :key="`${item.conversation_id}-${item.at}-${item.kind}`"
          class="flex flex-col gap-1 py-3 min-w-0 first:pt-0"
        >
          <div
            class="flex flex-wrap gap-x-2 gap-y-1 items-center text-xs text-n-slate-11"
          >
            <span
              class="px-1.5 py-0.5 font-medium rounded bg-n-ruby-3 text-n-ruby-11"
            >
              {{ kindLabel(item.kind) }}
            </span>
            <span>{{ dateTime(item.at, timezone) }}</span>
            <span v-if="agentNames[item.agent_slug]">
              {{ agentNames[item.agent_slug] }}
            </span>
            <ConversationLink
              v-if="item.conversation_id"
              :conversation-id="item.conversation_id"
              :deleted="item.conversation_exists === false"
              class="ms-auto"
            />
          </div>
          <p
            v-if="item.detail"
            class="mb-0 text-xs break-words line-clamp-2 text-n-slate-11"
          >
            {{ item.detail }}
          </p>
        </li>
      </ul>
    </div>
  </AnalyticsPanel>
</template>
